import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase';
import { ref, onValue, update } from 'firebase/database';
import { toast } from 'react-toastify';
import { FaFilePdf, FaArrowLeft } from 'react-icons/fa';

const AuditChallanDetails = () => {
    const { type, userId, paymentId, subType } = useParams();
    const navigate = useNavigate();
    
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [paymentData, setPaymentData] = useState(null);
    const [allWorks, setAllWorks] = useState({});
    
    const [updateStatus, setUpdateStatus] = useState('Approved');
    const [updateRemarks, setUpdateRemarks] = useState('');
    const [isUpdating, setIsUpdating] = useState(false);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (!user) navigate('/login');
        });

        // 1. Fetch available works for lookup
        const worksRef = ref(db, 'ekamwatapzp/WorkNames');
        onValue(worksRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const worksMap = {};
                
                Object.entries(data).forEach(([key, val]) => {
                    if (val && typeof val === 'object') {
                        // Check if it's a work object directly (Flat structure)
                        if (val.id || val.workId || val.workNumber) {
                            worksMap[val.workId || val.workNumber || val.id] = val;
                        }
                        
                        // Check if it's a category/date node (Nested structure)
                        Object.values(val).forEach(innerVal => {
                            if (innerVal && typeof innerVal === 'object') {
                                if (innerVal.id || innerVal.workId || innerVal.workNumber) {
                                    worksMap[innerVal.workId || innerVal.workNumber || innerVal.id] = innerVal;
                                }
                            }
                        });
                    }
                });
                setAllWorks(worksMap);
            }
        });

        // 2. Fetch User Data from Old/New Nodes
        const findUserAndPayment = async () => {
            const nodes = type === 'engineer' 
                ? ['ekamwatapzp/EngineerRegistrations', 'ekamwatapzp/EngineerLotteryRegistrations']
                : ['ekamwatapzp/SocietyRegistrations', 'ekamwatapzp/SocietyLotteryRegistrations'];

            let foundUser = null;
            let foundPayment = null;
            let foundSource = '';

            for (const nodePath of nodes) {
                const userRef = ref(db, `${nodePath}/${userId}`);
                await new Promise(resolve => {
                    onValue(userRef, (snapshot) => {
                        if (snapshot.exists()) {
                            const data = snapshot.val();
                            foundUser = data;
                            foundSource = nodePath;
                            
                            // Extract Payment
                            if (subType === 'interested') {
                                foundPayment = data.work_payment?.[paymentId];
                            } else {
                                foundPayment = data.payments?.[paymentId];
                            }
                        }
                        resolve();
                    }, { onlyOnce: true });
                });
                if (foundUser) break;
            }

            if (foundUser && foundPayment) {
                setUserData({ ...foundUser, sourceNode: foundSource });
                setPaymentData(foundPayment);
                setUpdateStatus(foundPayment.status || 'Approved');
                setUpdateRemarks(foundPayment.remarks || '');
            } else {
                toast.error("डेटा सापडला नाही!");
                navigate(-1);
            }
            setLoading(false);
        };

        findUserAndPayment();

        return () => unsubscribe();
    }, [type, userId, paymentId, subType, navigate]);

    const handleUpdateStatus = async () => {
        setIsUpdating(true);
        try {
            const updatePath = subType === 'interested'
                ? `${userData.sourceNode}/${userId}/work_payment/${paymentId}`
                : `${userData.sourceNode}/${userId}/payments/${paymentId}`;

            await update(ref(db, updatePath), {
                status: updateStatus,
                remarks: updateRemarks
            });
            
            toast.success("स्थिती यशस्वीरित्या अपडेट केली!");
            navigate(-1);
        } catch (error) {
            console.error("Error updating status:", error);
            toast.error("अपडेट करताना त्रुटी आली!");
        } finally {
            setIsUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f4f7fe]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans text-gray-800">
            {/* Header section with Back Button */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-2 border-b border-gray-200 pb-4">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded text-xs font-bold text-gray-600 hover:bg-gray-50 transition shadow-sm"
                    >
                        <FaArrowLeft /> Back
                    </button>
                    <h1 className="text-xl md:text-2xl font-normal text-gray-800">वर्क रिपोर्टस्</h1>
                </div>
                <div className="text-[11px] md:text-sm font-medium text-gray-500">
                    सांगली जिल्हा परिषद  /  <span className="text-gray-400 font-bold">वर्क रिपोर्टस्</span>
                </div>
            </div>

            <div className="bg-white rounded shadow-sm border border-gray-100 p-6 md:p-10 max-w-7xl mx-auto min-h-[500px]">
                <h2 className="text-2xl font-bold text-gray-800 mb-6">Challan details</h2>

                <div className="mb-6">
                    <p className="text-sm font-bold text-gray-600 mb-2">Work Challan Details -</p>
                    <div className="w-10 h-10 border border-red-200 rounded flex items-center justify-center text-red-500 shadow-sm cursor-pointer hover:bg-red-50 transition">
                        <FaFilePdf size={24} />
                    </div>
                </div>

                <div className="overflow-x-auto mb-8 border border-gray-200 rounded">
                    <table className="w-full text-left">
                        <thead className="bg-white border-b border-gray-200">
                            <tr>
                                <th className="px-5 py-4 text-sm font-bold border-r">Sr.</th>
                                <th className="px-5 py-4 text-sm font-bold border-r">Work Id</th>
                                <th className="px-5 py-4 text-sm font-bold border-r">कामाचे नाव</th>
                                <th className="px-5 py-4 text-sm font-bold border-r">निविदा रक्कम</th>
                                <th className="px-5 py-4 text-sm font-bold">तालुका</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 italic">
                            {paymentData?.selectedWorks ? (
                                paymentData.selectedWorks.map((workStr, idx) => {
                                    const workId = workStr.split(' - ')[0].trim();
                                    const workDetails = allWorks[workId] || {};
                                    return (
                                        <tr key={idx} className="hover:bg-gray-50/50">
                                            <td className="px-5 py-4 border-r text-sm text-center">{idx + 1}</td>
                                            <td className="px-5 py-4 border-r text-sm font-normal">{workId}</td>
                                            <td className="px-5 py-4 border-r text-sm font-medium text-[#2a59e8]">
                                                {workDetails.workName || workStr.split(' - ')[1] || '-'}
                                            </td>
                                            <td className="px-5 py-4 border-r text-sm font-normal text-center">
                                                {workDetails.tenderAmount || workDetails.amount || '-'}
                                            </td>
                                            <td className="px-5 py-4 text-sm font-normal text-center">
                                                {workDetails.taluka || "-"}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="5" className="px-5 py-10 text-center text-gray-400 italic">कामाची माहिती उपलब्ध नाही.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="space-y-6 max-w-full">
                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700">Status <span className="text-red-500">*</span></label>
                        <select 
                            className="w-full p-2 border border-gray-300 rounded text-sm bg-white outline-none focus:ring-1 focus:ring-blue-500 transition"
                            value={updateStatus}
                            onChange={(e) => setUpdateStatus(e.target.value)}
                        >
                            <option value="Approved">Approved</option>
                            <option value="Pending">Pending</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-bold text-gray-700">Remarks</label>
                        <textarea 
                            className="w-full p-4 border border-gray-300 rounded text-sm h-28 outline-none focus:ring-1 focus:ring-blue-500 transition"
                            value={updateRemarks}
                            onChange={(e) => setUpdateRemarks(e.target.value)}
                        ></textarea>
                    </div>

                    <div className="flex gap-2 pt-4">
                        <button 
                            onClick={handleUpdateStatus}
                            disabled={isUpdating}
                            className="px-6 py-2 bg-[#4c6ef5] hover:bg-blue-700 text-white rounded text-sm font-bold transition shadow-sm"
                        >
                            {isUpdating ? "Updating..." : "Update Status"}
                        </button>
                        <button 
                            onClick={() => navigate(-1)}
                            className="px-6 py-2 bg-[#38b2ac] hover:bg-teal-600 text-white rounded text-sm font-bold transition shadow-sm"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AuditChallanDetails;
